import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { CuestionariosService } from '../services/cuestionarios.service';
import { IPregunta } from '../interfaces/pregunta.interface';

@Component({
  selector: 'app-nueva-pregunta-dialog',
  templateUrl: './nueva-pregunta-dialog.component.html',
  providers: [MessageService]
})
export class NuevaPreguntaDialogComponent implements OnInit, OnChanges {
  @Input() displayDialog: boolean = false;
  @Input() cuestionario: any = null;
  @Input() modoEdicion: boolean = false;
  @Input() preguntaSeleccionada: IPregunta | null = null;
  @Output() displayDialogChange = new EventEmitter<boolean>();
  @Output() onHide = new EventEmitter<{refresh: boolean}>();

  form: FormGroup;
  loading: boolean = false;
  esEdicion: boolean = false;
  displayOpcionesDialog: boolean = false;

  // Datos para los dropdowns
  tablas: { label: string, value: string }[] = [];
  tipos: { label: string, value: string }[] = [];
  campos: { label: string, value: string }[] = [];
  tiposRespuesta: { label: string, value: string }[] = [];

  // Mapeo de tablas a campos
  private tablaCampoMap = {
    'cmtbl_empleos': 'tipo_ingreso',
    'cmtbl_domicilios': 'tpo_domicilio',
    'cmtbl_refpers': 'tpo_ref',
    'cmtbl_refcome': 'tpo_referencia',
    'cmtbl_propiedades': 'tipo_propiedad',
    'tmprelacion': 'tipo'
  };

  displayRenumerarDialog: boolean = false;
  displayGuionesDialog: boolean = false;

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private cuestionariosService: CuestionariosService
  ) {
    // Para pregunta abierta solo son obligatorios: N° Pregunta, Pregunta, Tipo Respuesta y Grabar.
    // Tabla, Tipo y Campo son opcionales (para preguntas vinculadas a tablas).
    this.form = this.fb.group({
      sClaveCues: [{ value: '', disabled: true }, Validators.required],
      iNumPreg: ['', Validators.required],
      sQuePregunta: ['', Validators.required],
      sTabla: [''],
      sTipo: [''],
      sCampo: [''],
      iTipoResp: ['', Validators.required],
      bActualizar: [0],
      bMostrar: [0],
      bGrabar: [0]
    });

    // Suscribirse a cambios en sTabla
    this.form.get('sTabla')?.valueChanges.subscribe(tabla => {
      if (tabla) {
        this.cargarCampos(tabla);
        this.cargarTipos(tabla);
      } else {
        this.campos = [];
        this.tipos = [];
        this.form.get('sCampo')?.setValue('');
        this.form.get('sTipo')?.setValue('');
      }
    });
  }

  ngOnInit(): void {
    this.cargarTablas();
    this.cargarTiposRespuesta();
    this.cargarDatosIniciales();
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Cambios detectados:', changes);
    if (changes['displayDialog'] && this.displayDialog) {
      this.cargarDatosIniciales();
    }

    if (changes['preguntaSeleccionada'] && this.preguntaSeleccionada && this.modoEdicion) {
      this.cargarDatosPregunta();
    }
  }

  cargarDatosIniciales(): void {
    console.log('Cargando datos iniciales:', {
      cuestionario: this.cuestionario,
      preguntaSeleccionada: this.preguntaSeleccionada
    });

    if (this.cuestionario) {
      this.form.patchValue({
        sClaveCues: this.cuestionario.sClaveCues
      });

      if (this.preguntaSeleccionada) {
        console.log('Modo edición activado');
        this.esEdicion = true;
        this.cargarDatosPregunta();
      }
    }
  }

  cargarDatosPregunta(): void {
    if (!this.preguntaSeleccionada) return;

    // Primero cargamos la tabla para que se disparen los eventos de carga de campos y tipos
    this.cargarCampos(this.preguntaSeleccionada.sTabla);
    this.cargarTipos(this.preguntaSeleccionada.sTabla);

    // Esperamos un momento para asegurarnos de que los dropdowns se hayan cargado
    setTimeout(() => {
      this.form.patchValue({
        iNumPreg: this.preguntaSeleccionada.iNumPreg,
        sQuePregunta: this.preguntaSeleccionada.sQuePregunta,
        sTabla: this.preguntaSeleccionada.sTabla,
        sTipo: this.preguntaSeleccionada.sTipo,
        sCampo: this.preguntaSeleccionada.sCampo,
        iTipoResp: this.preguntaSeleccionada.iTipoResp.toString(),
        bActualizar: this.preguntaSeleccionada.bActualizar,
        bMostrar: this.preguntaSeleccionada.bMostrar,
        bGrabar: this.preguntaSeleccionada.bGrabar
      });
    }, 500);
  }

  cargarTablas(): void {
    this.loading = true;
    this.cuestionariosService.getTablas().subscribe({
      next: (response) => {
        if (response.success) {
          this.tablas = response.data.map(item => ({
            label: `${item.sTabla}`,
            value: item.sTabla
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar tablas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar las tablas'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cargarCampos(sTabla: string): void {
    this.loading = true;
    this.cuestionariosService.getTipos(sTabla).subscribe({
      next: (response) => {
        if (response.success) {
          this.campos = response.data.map(item => ({
            label: item.sEtiqueta,
            value: item.sCampo
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar campos:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los campos'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  cargarTipos(sTabla: string): void {
    this.loading = true;
    const campo = this.tablaCampoMap[sTabla.toLowerCase()];

    if (campo) {
      // Si la tabla está en el mapeo, usar GetOpcionesVariables
      this.cuestionariosService.getTiposPorTabla(sTabla, campo).subscribe({
        next: (response) => {
          if (response.success) {
            this.tipos = response.data.map(item => ({
              label: item.sOpcion,
              value: item.sValor
            }));
          }
        },
        error: (error) => {
          console.error('Error al cargar tipos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los tipos'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      // Si no está en el mapeo, usar Customizables
      this.cuestionariosService.getCustomizables().subscribe({
        next: (response) => {
          if (response.success) {
            this.tipos = response.data.map(item => ({
              label: item.descripcion,
              value: item.caracteristica
            }));
          }
        },
        error: (error) => {
          console.error('Error al cargar tipos:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar los tipos'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    }
  }

  cargarTiposRespuesta(): void {
    this.loading = true;
    this.cuestionariosService.getTiposPorTabla('cmtbl_preguntas', 'iTipoResp').subscribe({
      next: (response) => {
        if (response.success) {
          this.tiposRespuesta = response.data.map(item => ({
            label: `${item.sValor} | ${item.sOpcion}`,
            value: item.sValor
          }));
        }
      },
      error: (error) => {
        console.error('Error al cargar tipos de respuesta:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los tipos de respuesta'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      const formValue = this.form.getRawValue();
      
      const request = {
        que: this.esEdicion ? 2 : 1,
        sClaveCues: this.cuestionario.sClaveCues,
        iNumPreg: formValue.iNumPreg,
        sQuePregunta: formValue.sQuePregunta,
        sTabla: formValue.sTabla,
        sTipo: formValue.sTipo,
        sCampo: formValue.sCampo,
        iTipoResp: parseInt(formValue.iTipoResp),
        bActualizar: formValue.bActualizar ? 1 : 0,
        bMostrar: formValue.bMostrar ? 1 : 0,
        bGrabar: formValue.bGrabar ? 1 : 0
      };

      console.log('Enviando request:', request);

      this.cuestionariosService.guardarPregunta(request).subscribe({
        next: (response) => {
          if (response.success && response.data.error === 0) {
            this.messageService.add({
              severity: 'success',
              summary: 'Éxito',
              detail: this.esEdicion ? 'Pregunta actualizada correctamente' : 'Pregunta guardada correctamente'
            });
            this.onDialogClosed();
            this.onHide.emit({ refresh: true });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.esEdicion ? 'No se pudo actualizar la pregunta' : 'No se pudo guardar la pregunta'
            });
          }
        },
        error: (error) => {
          console.error('Error:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.esEdicion ? 'No se pudo actualizar la pregunta' : 'No se pudo guardar la pregunta'
          });
        },
        complete: () => {
          this.loading = false;
        }
      });
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Por favor complete todos los campos requeridos'
      });
    }
  }

  limpiarFormulario(): void {
    this.form.reset({
      sClaveCues: this.cuestionario?.sClaveCues || '',
      bActualizar: 0,
      bMostrar: 0,
      bGrabar: 0
    });
    this.campos = [];
    this.tipos = [];
    this.esEdicion = false;
    this.preguntaSeleccionada = null;
  }

  onDialogClosed(): void {
    this.displayDialog = false;
    this.displayDialogChange.emit(this.displayDialog);
    this.onHide.emit({ refresh: false });
    this.limpiarFormulario();
  }

  renumerarPregunta(): void {
    this.displayRenumerarDialog = true;
  }

  onRenumerarDialogClosed(event: {refresh: boolean}): void {
    this.displayRenumerarDialog = false;
    if (event?.refresh) {
      this.onHide.emit({ refresh: true });
    }
  }

  abrirGuiones(): void {
    this.displayGuionesDialog = true;
  }

  onGuionesDialogClosed(event: {refresh: boolean}): void {
    this.displayGuionesDialog = false;
    if (event.refresh) {
      // TODO: Implementar refresh si es necesario
    }
  }

  openOpciones(): void {
    if (!this.form.get('iNumPreg')?.value) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'Debe guardar la pregunta primero para gestionar sus opciones'
      });
      return;
    }
    this.displayOpcionesDialog = true;
  }

  onOpcionesDialogClosed(event: {refresh: boolean}): void {
    this.displayOpcionesDialog = false;
  }
} 